import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Divider } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import type { StandardDTO } from '../models/standardsDTO';
import {
    ControlledTextField,
    ControlledCheckbox,
    ControlledSingleSelect,
} from '@/src/ui/ControlledForm';
import { SimpleButton, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type { Option } from '@/src/utils';
import Constants from '@/src/utils/Constants';

interface StandardsModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    initialData?: StandardDTO | null;
}

const schema = yup.object({
    name: yup
        .string()
        .required('Name is required')
        .max(Constants.MAX_CHARS.NAME, `Name cannot exceed ${Constants.MAX_CHARS.NAME} characters`),
    gradeLevelMasterId: yup
        .number()
        .required('Grade Level is required')
        .min(1, 'Select a grade level'),
    sortOrder: yup
        .number()
        .typeError('Sort Order must be a number')
        .integer('Sort Order must be an integer')
        .min(0, 'Sort Order must be 0 or greater')
        .required('Sort Order is required'),
    practiceVideoURL: yup
        .string()
        .max(
            Constants.MAX_CHARS.URL_PATH,
            `URL cannot exceed ${Constants.MAX_CHARS.URL_PATH} characters`
        ),
    isPracticeAllowed: yup.boolean().required(),
    practiceLessonName: yup
        .string()
        .max(
            Constants.MAX_CHARS.TITLE,
            `Lesson name cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    standardDomainMasterId: yup
        .number()
        .typeError('Domain Id must be a number')
        .integer('Domain Id must be an integer')
        .min(0, 'Domain Id must be 0 or greater')
        .required('Domain is required'),
    isSubstandard: yup.boolean().required(),
});

const StandardsModal: React.FC<StandardsModalProps> = ({ open, onClose, initialData }) => {
    const isEdit = Boolean(initialData && initialData.id);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [gradeLevelOptions, setGradeLevelOptions] = useState<Option[]>([]);
    const [loadingGradeLevels, setLoadingGradeLevels] = useState(false);
    const [gradeLevelError, setGradeLevelError] = useState<string | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: initialData || {
            name: '',
            gradeLevelMasterId: 0,
            status: 1,
            sortOrder: 0,
            practiceVideoURL: '',
            isPracticeAllowed: false,
            practiceLessonName: '',
            standardDomainMasterId: 0,
            isSubstandard: false,
        },
        mode: 'onTouched',
    });

    useEffect(() => {
        if (open) {
            reset(
                initialData || {
                    name: '',
                    gradeLevelMasterId: 0,
                    status: 1,
                    sortOrder: 0,
                    practiceVideoURL: '',
                    isPracticeAllowed: false,
                    practiceLessonName: '',
                    standardDomainMasterId: 0,
                    isSubstandard: false,
                }
            );
        }
    }, [initialData, open, reset]);

    useEffect(() => {
        setLoadingGradeLevels(true);
        apiRequest('GET', 'utility/filterData?getGradeLevelMaster=true')
            .then((res: any) => {
                if (res && Array.isArray(res.data?.gradeLevelMasterOptions)) {
                    setGradeLevelOptions(res.data.gradeLevelMasterOptions);
                } else {
                    setGradeLevelOptions([]);
                }
            })
            .catch(() => setGradeLevelError('Failed to load grade levels'))
            .finally(() => setLoadingGradeLevels(false));
    }, []);

    const handleSave = async (standard: any) => {
        setError('');
        setSubmitting(true);
        try {
            if (isEdit) {
                await apiRequest('PUT', `standards/${initialData?.id}`, {
                    ...initialData,
                    ...standard,
                });
            } else {
                await apiRequest('POST', 'standards', standard);
            }
            setSubmitting(false);
            onClose(true);
        } catch {
            setError(isEdit ? 'Failed to update standard' : 'Failed to create standard');
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => onClose(false)}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    p: 4,
                    background: 'white',
                    borderRadius: 2,
                    maxWidth: 600,
                    width: '90%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    {initialData ? 'Edit Standard' : 'Create Standard'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <form onSubmit={handleSubmit(handleSave)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <ControlledTextField
                            name="name"
                            label="Name"
                            control={control}
                            error={errors.name}
                            maxLength={Constants.MAX_CHARS.TITLE}
                        />
                        <ControlledSingleSelect
                            name="gradeLevelMasterId"
                            label="Grade Level"
                            control={control}
                            options={gradeLevelOptions}
                            error={errors.gradeLevelMasterId || gradeLevelError}
                            disabled={loadingGradeLevels}
                        />
                        <ControlledTextField
                            name="sortOrder"
                            label="Sort Order"
                            control={control}
                            error={errors.sortOrder}
                            type="number"
                        />
                        <ControlledTextField
                            name="practiceVideoURL"
                            label="Practice Video URL"
                            control={control}
                            error={errors.practiceVideoURL}
                            maxLength={Constants.MAX_CHARS.URL_PATH}
                        />
                        <ControlledTextField
                            name="practiceLessonName"
                            label="Practice Lesson Name"
                            control={control}
                            error={errors.practiceLessonName}
                            maxLength={Constants.MAX_CHARS.TITLE}
                        />
                        <ControlledTextField
                            name="standardDomainMasterId"
                            label="Domain Id"
                            control={control}
                            error={errors.standardDomainMasterId}
                            type="number"
                        />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <ControlledCheckbox
                                name="isPracticeAllowed"
                                label="Practice Allowed"
                                control={control}
                            />
                            <ControlledCheckbox
                                name="isSubstandard"
                                label="Is Substandard"
                                control={control}
                            />
                        </Box>
                        <ErrorMessage error={error} />
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                            <SimpleButton type="submit" variant="contained" disabled={submitting}>
                                {isEdit ? 'Update' : 'Create'}
                            </SimpleButton>
                            <SimpleButton
                                onClick={() => onClose(false)}
                                color="secondary"
                                variant="outlined"
                                disabled={submitting}
                            >
                                Cancel
                            </SimpleButton>
                        </Box>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default StandardsModal;
